import { InboxCoverage } from "entities/domain/inbox/managed-inbox-domain";

/**
 * One entry of an Inbox's access list as stored (entities/dto/ManagedInboxDTO
 * InboxGrantDTO): one person.
 */
export interface InboxGrant {
  id: string;
  agentId: number;
  agentName: string | null;
  source: string;
  createdAt: string;
  createdByAgentId: number | null;
}

/** One active member, once. */
export interface InboxPerson {
  agentId: number;
  name: string | null;
  isEligibleForUnassignedAlerts: boolean;
}

/**
 * An Inbox's members (Settings → Inboxes → Edit): the stored grants, the
 * active people they resolve to, and the server's coverage. `version` is the
 * Inbox's own version when this was read.
 */
export default class InboxMembersDomain {
  inboxId: string;

  version: number;

  // False for an Inbox an old server image created: the server resolves its
  // people another way until the first member edit writes its grants.
  grantsInitialized: boolean;

  grants: InboxGrant[];

  // Active members only. A deactivated person can still hold a grant.
  people: InboxPerson[];

  // Null for an archived Inbox.
  coverage: InboxCoverage | null;

  constructor(
    inboxId: string,
    version: number,
    grantsInitialized: boolean,
    grants: InboxGrant[],
    people: InboxPerson[],
    coverage: InboxCoverage | null
  ) {
    this.inboxId = inboxId;
    this.version = version;
    this.grantsInitialized = grantsInitialized;
    this.grants = grants;
    this.people = people;
    this.coverage = coverage;
  }

  // People granted, active or not.
  grantedAgentIds(): number[] {
    return this.grants.map((grant) => grant.agentId);
  }

  isAgentGranted(agentId: number): boolean {
    return this.grants.some((grant) => grant.agentId === agentId);
  }

  // `people` lists active members only, so a person's grant without them is
  // a deactivated person's.
  isActiveMember(agentId: number): boolean {
    return this.people.some((person) => person.agentId === agentId);
  }
}
